import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, Input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TableModule } from 'src/app/table/table.module';
import { TRANSLATION_MAP } from 'src/app/enums';
import { TableComponent } from 'src/app/table/table.component';
import { BehaviorSubject } from 'rxjs';
import { IRowActionGetter, IRowClassNamesGetter, ITableData } from 'src/app/table/interfaces';
import { MULTI_REGISTRATION_ATTRIBUTE_NAMES, TableRowObject, UI_PARTICIPANT_STATUS_TRANSLATION, DraftSubParticipantRecord } from './types';
import {
    MultiRegistrationConfig,
    TicketOption,
    UIParticipant,
    UIParticipantStatus,
    UIState,
} from '@azavista/participants/dist/event-app/multiregistration/';
import { getMapByGetter } from '../utils.service';
import { SharedService } from '../shared.service';
import { MultiRegistrationFormComponent } from './multi-registration-form.component';
import { IField, IObjectWithId } from '../interfaces';
import { MultiRegistrationFormService } from './multi-registration-form.service';
import { getIsRequireTicket } from './utils';

@Component({
    selector: 'aza-rws-multi-registration-form-table',
    imports: [CommonModule, TranslateModule, TableModule],
    templateUrl: `./multi-registration-form.table.component.html`,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiRegistrationFormTableComponent
    implements
        Pick<
            TableComponent<TableRowObject>,
            'extraRowDivTemplate' | 'rowActionGetter'
        >,
        Pick<
            MultiRegistrationFormComponent,
            'config' | 'state' | 'subParticipantFields'
        >
{
    private _state: UIState;
    public get state(): UIState {
        return this._state;
    }

    @Input({
        required: true,
    })
    public set state(value: UIState) {
        this._state = value;
        this.loadTableData();
    }

    @Input({
        required: true,
    })
    extraRowDivTemplate?: TableComponent['extraRowDivTemplate'];

    @Input({
        required: true,
    })
    rowActionGetter: IRowActionGetter<TableRowObject>;

    @Input({
        required: true,
    })
    config: MultiRegistrationConfig;

    @Input({
        required: true,
    })
    eventParticipant?: IObjectWithId;

    private _subParticipantFields: IField[] = [];
    public get subParticipantFields(): IField[] {
        return this._subParticipantFields;
    }

    @Input({
        required: true,
    })
    public set subParticipantFields(value: IField[]) {
        this._subParticipantFields = value;
        this.loadTableData();
    }

    @Input({
        required: true,
    })
    editedSubParticipantRecord: DraftSubParticipantRecord;

    data$ = new BehaviorSubject<ITableData<TableRowObject>>({
        pagedResponse: {
            limit: 0,
            object_count: 0,
            offset: 0,
        },
        rowObjects: [],
    });

    TRANSLATION_MAP = TRANSLATION_MAP;

    constructor(
        private sharedSvc: SharedService,
        private formSvc: MultiRegistrationFormService,
    ) {}

    private loadTableData() {
        const data = this.getTableDataData$params();
        if (data) {
            this.data$.next(data);
        }
    }

    private getTableDataData$params(): ITableData<TableRowObject> | undefined {
        if (!this.state) {
            return undefined;
        }
        const getRowObjectItem = (
            participant: UIParticipant
        ): TableRowObject => ({
            ...participant.participant_attributes,
            ...participant,
            ui_participant: participant,
            [MULTI_REGISTRATION_ATTRIBUTE_NAMES.tableColumnStatusText]: participant.status && this.sharedSvc.translate(UI_PARTICIPANT_STATUS_TRANSLATION[participant.status]),
        });
        const allSubParticipants: UIParticipant[] = [];
        if (this.state.main_participant.ticket_type_id) {
            const main_participant: UIParticipant = {
                ...this.state.main_participant,
            };
            main_participant.participant_attributes = {
                ...this.sharedSvc.clone(this.eventParticipant || {}),
                ...main_participant.participant_attributes,
            };
            allSubParticipants.push(main_participant);
        }
        allSubParticipants.push(
            ...this.state.subparticipants
        );
        const rowObjects = allSubParticipants.map(getRowObjectItem);
        const columns: IField[] = [
            {
                id: MULTI_REGISTRATION_ATTRIBUTE_NAMES.tableColumnStatusText,
                name: MULTI_REGISTRATION_ATTRIBUTE_NAMES.tableColumnStatusText,
                label: this.sharedSvc.translate(TRANSLATION_MAP.STATUS),
                editable: false,
            } as IField,
            ...this.subParticipantFields,
        ];
        if (getIsRequireTicket(this.config)) {
            const allTicketOptions: TicketOption[] = [];
            rowObjects.forEach((rowParticipant) => {
                const allTicketOptionsMap = getMapByGetter(
                    allTicketOptions,
                    (opt) => opt.value
                );
                rowParticipant.ui_participant.ticket_options.forEach((opt) => {
                    if (!allTicketOptions[opt.value]) {
                        allTicketOptions.push(opt);
                    }
                });
            });
            columns.push(
                this.formSvc.createTicketField({
                    ticket_options: allTicketOptions,
                    status: UIParticipantStatus.PENDING_ASSIGNMENT,
                    ticket_type_id: null,
                    editable: true,
                })
            );
        }
        return {
            columns,
            rowObjects,
            pagedResponse: {
                limit: rowObjects.length,
                object_count: rowObjects.length,
                offset: 0,
            },
        };
    }

    rowClassNamesGetter: IRowClassNamesGetter<TableRowObject> = (row) => {
        const ui_participant = row.ui_participant;
        const classNames: string[] = [`row-id--${row.id}`, `row-status--${ui_participant.status}`];
        if (ui_participant.is_main) {
            classNames.push('row-main-participant');
        }
        if (this.editedSubParticipantRecord[row.id] || ui_participant.error?.code) {
            classNames.push('aza-table-table-view-body-row--border-none');
        }
        return classNames;
    };
}
